import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import { getCdnslamFundamentoLegal, getTenantOrgIdentificacao } from "../utils/tenantDocumentConfig";

const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingBottom: 40,
    paddingHorizontal: 50,
    fontFamily: "Helvetica",
    fontSize: 10,
    lineHeight: 1.5,
  },
  headerImage: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
  },
  footerImage: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
  },
  documentTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 12,
    textAlign: "center",
    marginBottom: 18,
  },
  registro: {
    fontSize: 10,
    marginBottom: 16,
  },
  mainText: {
    textAlign: "justify",
    marginBottom: 20,
    fontSize: 10,
  },
  bold: {
    fontFamily: "Helvetica-Bold",
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    marginVertical: 10,
  },
  dataBlock: {
    marginBottom: 4,
  },
  dataLabel: {
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
  },
  dataValue: {
    fontSize: 10,
  },
  sectionTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    textDecoration: "underline",
    marginBottom: 8,
  },
  consideracoesIntro: {
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    textAlign: "justify",
    marginBottom: 10,
  },
  item: {
    fontSize: 10,
    textAlign: "justify",
    marginBottom: 8,
  },
  observacaoTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 10,
    marginTop: 10,
    marginBottom: 6,
  },
  observacao: {
    fontSize: 10,
    textAlign: "justify",
  },
  qrFixed: {
    position: "absolute",
    bottom: 45,
    right: 10,
    alignItems: "center",
  },
  qrFixedImage: {
    width: 60,
    height: 60,
  },
  qrFixedLabel: {
    fontSize: 7,
    textAlign: "center",
    marginTop: 2,
  },
});

function CDNSLAMDocument({
  empreendimento,
  atividadeLabel,
  atividadeEmpreendimento,
  endereco,
  headerImgSrc,
  footerImgSrc,
  qrCodeDataUrl,
  numeroProcesso,
}) {
  const nomeRazaoSocial = empreendimento?.nome || "Não informado";
  const cpfCnpj = empreendimento?.cnpj || "Não informado";
  const atividade = atividadeLabel || "Não informado";
  const enderecoCompleto = endereco || "Não informado";
  const orgIdentificacao = getTenantOrgIdentificacao();
  const fundamentoLegal = getCdnslamFundamentoLegal(atividadeEmpreendimento);

  const pageStyle = [
    styles.page,
    headerImgSrc ? { paddingTop: 80 } : {},
    footerImgSrc ? { paddingBottom: 80 } : {},
  ];

  return (
    <Document>
      <Page size="A4" style={pageStyle}>
        {headerImgSrc && (
          <Image src={headerImgSrc} style={styles.headerImage} fixed />
        )}
        {footerImgSrc && (
          <Image src={footerImgSrc} style={styles.footerImage} fixed />
        )}
        <Text style={styles.documentTitle}>
          Certidão Declaratória de Não Sujeição ao Licenciamento Ambiental
        </Text>
        {/* Registro */}
        <Text style={styles.registro}>
          <Text style={styles.bold}>Registro N°:</Text> {numeroProcesso || "—"}
        </Text>

        {/* Corpo principal */}
        <Text style={styles.mainText}>
          <Text style={styles.bold}>
            {orgIdentificacao.nomeAbreviado}
          </Text>
          {orgIdentificacao.descricao}, concede a presente{" "}
          <Text style={styles.bold}>
            CERTIDÃO DECLARATÓRIA DE NÃO SUJEIÇÃO DO EMPREENDIMENTO OU DA
            ATIVIDADE AO LICENCIAMENTO AMBIENTAL
          </Text>
          {`, em conformidade com o artigo ${fundamentoLegal.artigo}${fundamentoLegal.trechoTextoPrincipal}, de 8`}
          de agosto de 2025, que dispõe sobre as hipóteses de não sujeição ao
          licenciamento ambiental e a emissão de certidão declaratória pelo
          órgão ambiental competente.
        </Text>

        {/* Dados do empreendimento */}
        <View style={styles.divider} />
        <View style={styles.dataBlock}>
          <Text>
            <Text style={styles.dataLabel}>NOME/RAZÃO SOCIAL: </Text>
            <Text style={styles.dataValue}>{nomeRazaoSocial}</Text>
          </Text>
          <Text>
            <Text style={styles.dataLabel}>CPF/CNPJ: </Text>
            <Text style={styles.dataValue}>{cpfCnpj}</Text>
          </Text>
        </View>
        <View style={styles.divider} />

        <View style={styles.dataBlock}>
          <Text style={styles.dataLabel}>ATIVIDADE DO EMPREENDIMENTO:</Text>
          <Text style={styles.dataValue}>{atividade}</Text>
        </View>
        <Text style={styles.dataLabel}>ENDEREÇO DO EMPREENDIMENTO:</Text>
        <Text style={styles.dataValue}>{enderecoCompleto}</Text>
        <View style={styles.divider} />

        <View style={styles.dataBlock}>
          <Text style={styles.dataLabel}>
            HIPÓTESE LEGAL DE NÃO SUJEIÇÃO AO LICENCIAMENTO AMBIENTAL:
          </Text>
          <Text style={styles.dataValue}>{fundamentoLegal.hipoteseLegal}</Text>
        </View>
        <View style={styles.divider} />

        {/* Considerações */}
        <Text style={styles.sectionTitle}>CONSIDERAÇÕES:</Text>

        <Text style={styles.consideracoesIntro}>
          Em que pese a previsão legal de não sujeição ao licenciamento
          ambiental, o requerente acima identificado fica ciente das seguintes
          considerações e determinações:
        </Text>

        <Text style={styles.item}>
          1. A presente certidão possui natureza estritamente declaratória,
          emitida no âmbito dos instrumentos da política de licenciamento
          ambiental previstos na Lei nº 15.190, de 8 de agosto de 2025;
        </Text>

        <Text style={styles.item}>
          2. Esta certidão não dispensa nem substitui quaisquer alvarás,
          licenças, outorgas ou autorizações de qualquer natureza eventualmente
          exigidos pela legislação federal, estadual ou municipal, tampouco
          exime o interessado do cumprimento das demais obrigações previstas na
          legislação vigente;
        </Text>

        <Text style={styles.item}>
          3. O empreendimento permanece sujeito à fiscalização pelos
          entes/órgãos ambientais competentes, bem como à aplicação das sanções
          cabíveis, inclusive as previstas nas Leis nº 15.190/2025 e nº
          9.605/1998, e no Decreto Federal nº 6.514/2008;
        </Text>

        <Text style={styles.item}>
          4. A não sujeição ao licenciamento ambiental não dispensa a obtenção
          de autorização ambiental específica para intervenções que dependam de
          controle ambiental, tais como supressão de vegetação, intervenção em
          Área de Preservação Permanente ou uso de recursos hídricos, dentre
          outras;
        </Text>

        <Text style={styles.item}>
          5. Esta certidão não autoriza intervenções em APP ou supressão de
          vegetação, as quais só serão permitidas em casos excepcionais de
          utilidade pública, interesse social ou baixo impacto ambiental,
          conforme a Lei Federal nº 12.651/2012 (Código Florestal), mediante
          procedimento administrativo específico;
        </Text>

        <Text style={styles.item}>
          6. A veracidade das informações e documentos apresentados é de
          inteira responsabilidade do requerente, sujeitando-o às sanções
          administrativas, civis e penais cabíveis, dentre as quais as
          previstas no art. 57 da Lei nº 15.190/2025 e no art. 299 do Código
          Penal;
        </Text>

        <Text style={styles.item}>
          7. Esta certidão não gera direito adquirido e poderá ser revista ou
          anulada pela Administração Pública em caso de erro, omissão ou
          falsidade das informações prestadas, sem prejuízo da responsabilização
          do requerente;
        </Text>

        <Text style={styles.item}>
          8. Qualquer alteração no empreendimento ou atividade que implique
          modificação de porte, potencial poluidor ou tipologia, resultando em
          enquadramento em atividade sujeita ao licenciamento ambiental, obriga
          o interessado a requerer o devido licenciamento junto ao órgão
          ambiental competente;
        </Text>

        <Text style={styles.item}>
          9. A presente certidão refere-se exclusivamente ao empreendimento ou
          atividade descritos neste documento, não sendo extensiva a outras
          atividades desenvolvidas na mesma área.
        </Text>

        {/* Observação */}
        <Text style={styles.observacaoTitle}>Observação:</Text>
        <Text style={styles.observacao}>
          Considerando que as hipóteses de não sujeição ao licenciamento
          ambiental previstas na Lei nº 15.190/2025 são objeto de
          questionamento no âmbito das Ações Diretas de Inconstitucionalidade
          nº 7913, 7916 e 7919, em trâmite no Supremo Tribunal Federal – STF,
          esta certidão poderá ser revista ou revogada pela Administração
          Pública caso venha a ser declarada a inconstitucionalidade das
          referidas disposições legais, nos termos da Súmula nº 473 do STF.
        </Text>

        {/* QR Code fixo em todas as páginas — canto inferior direito */}
        {qrCodeDataUrl && (
          <View fixed style={styles.qrFixed}>
            <Image src={qrCodeDataUrl} style={styles.qrFixedImage} />
            <Text style={styles.qrFixedLabel}>Escaneie para validar</Text>
          </View>
        )}
      </Page>
    </Document>
  );
}

export default CDNSLAMDocument;
